import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { LightButton, PrimaryButton } from "../../../../components/buttons/buttons";
import EmailControl from "../../../../components/controls/email-control/email-control";
import MoneyControl from "../../../../components/controls/money-control/money-control";
import MultilineControl from "../../../../components/controls/multiline-control/multiline-control";
import NumericControl from "../../../../components/controls/numeric-control/numeric-control";
import PhoneControl from "../../../../components/controls/phone-control/phone-control";
import RfcCurpControl from "../../../../components/controls/rfc-curp-control/rfc-curp-control";
import SelectControl from "../../../../components/controls/select-control/select-control";
import TextControl from "../../../../components/controls/text-control/text-control";
import Modal from "../../../../components/modals/modal/modal";
import { apply, continueWhen, empty, handleError, notEmpty, toSelectOption } from "../../../../core/utils";
import { ModalProps, createModal, displayError } from "../../../../core/utils/modals";
import useOnce from "../../../../hooks/use-once";
import { ActionHandler, SelectOption } from "../../../../types";
import { descargarDomiciliacion as apiDescargarDomiciliacion, domiciliarPago as apiDomiciliarPago, obtenerBancos as apiObtenerBancos, obtenerReferencia as apiObtenerReferencia } from "../api/pagos.api";
import { BancoRespuesta, DatosDomiciliacion, DatosReferencia, SolicitudDomiciliacion } from "../types/pagos.types";
import DomiciliacionExitosaModal from "./domiciliacion-exitosa.modal";
import "./domiciliar-pago.modal.scss";

const tipoIdentificacion: SelectOption[] = [
	{ value: "0", label: "INE" },
	{ value: "1", label: "Pasaporte" },
	{ value: "2", label: "Acta constitutiva y poderes" }
];

const tipoPersona: SelectOption[] = [
	{ value: "0", label: "Moral" },
	{ value: "1", label: "Física" }
];

const bancosToOption = (banco: BancoRespuesta) => toSelectOption(banco.clave, banco.nombre);

const desplegarDomiciliacionExitosa = (descargarComprobante: ActionHandler<DatosDomiciliacion>, onClosed: VoidFunction) => (comprobante: DatosDomiciliacion) =>
	createModal(DomiciliacionExitosaModal, {
		datos: comprobante,
		descargarComprobante: apply(descargarComprobante, comprobante),
		onClosed
	});

const mapearDatosPago = (datos: DatosReferencia): Partial<DatosDomiciliacion> => {
	return {
		nombre: datos.nombre,
		correoElectronico: datos.correoElectronico,
		telefono: datos.telefono,
		rfcCurp: datos.rfc,
		montoMaximo: datos.importe,
		concepto: datos.conceptos.join(", ").substring(0, 500)
	};
};

const useDomiciliacion = (numeroReferencia: string) => {
	const [datosPago, setDatosPago] = useState<Partial<DatosReferencia>>();
	const [bancos, setBancos] = useState<BancoRespuesta[]>([]);
	const [cargandoDatos, setCargandoDatos] = useState(false);

	const inicializar = () => {
		setCargandoDatos(true);

		apiObtenerReferencia(numeroReferencia)
			.then(continueWhen(notEmpty))
			.then(mapearDatosPago)
			.then(setDatosPago)
			.catch(handleError)
			.finally(apply(setCargandoDatos, false));
	};

	const obtenerBancos = () => {
		setCargandoDatos(true);
		setBancos([]);

		return apiObtenerBancos()
			.then(setBancos)
			.catch(displayError)
			.finally(apply(setCargandoDatos, false));
	};

	const descargarComprobante = (registro: DatosDomiciliacion) => {
		setCargandoDatos(true);

		apiDescargarDomiciliacion(registro.numeroReferencia)
			.catch(displayError)
			.finally(apply(setCargandoDatos, false));
	};

	const guardar = (solicitud: SolicitudDomiciliacion) => {
		setCargandoDatos(true);

		apiDomiciliarPago({ ...solicitud, numeroReferencia })
			.then(desplegarDomiciliacionExitosa(descargarComprobante, empty))
			.catch(displayError)
			.finally(apply(setCargandoDatos, false));
	};

	useOnce(() => {
		obtenerBancos()
			.then(inicializar);
	});

	return {
		cargandoDatos,
		bancos,
		datosPago,
		guardar
	};
};

type FormaDomiciliacion = SolicitudDomiciliacion;

export type DomiciliarPagoModalProps = ModalProps & {
	numeroReferencia: string;
};

const DomiciliarPagoModal = ({ numeroReferencia, parent, onClosed }: DomiciliarPagoModalProps) => {
	const methods = useForm<FormaDomiciliacion>();
	const {
		cargandoDatos,
		bancos,
		datosPago,
		guardar
	} = useDomiciliacion(numeroReferencia);

	const bancosOptions = bancos.map(bancosToOption);

	const footer = (close: VoidFunction) => {
		const continuarHandler = (forma: FormaDomiciliacion) => {
			guardar(forma);
			close();
		};

		return (
			<>
				<LightButton disabled={cargandoDatos} className="me-5" onClick={close}>Cancelar</LightButton>
				<PrimaryButton disabled={cargandoDatos} className="me-5" onClick={methods.handleSubmit(continuarHandler)}>Continuar</PrimaryButton>
			</>
		);
	};

	useEffect(() => {
		methods.reset({ ...datosPago });
	}, [datosPago, methods]);

	useOnce(() => {
		methods.setFocus("nombre");
	});

	return (
		<Modal parent={parent} title="" dialogClassName="domiciliar-pago text-left" footer={footer} onClosed={onClosed}>
			<div className="col-12">
				<div className="comprobante-banorte">
					<div className="c-header">
						<span>Domiciliar pago</span>
					</div>
					<div className="c-body">
						<div className="row">
							<div className="col-12">
								<div className="c-b-success">
									<div className="e-title text-left m-3">
										<span>Ingresa tus datos para domiciliar tu pago</span>
									</div>
								</div>
								<div className="row mt-3 m-auto">
									<div className="col-6">
										<TextControl
											name="nombre"
											label="Nombre completo"
											maxLength={60}
											formMethods={methods}
											pattern={{
												message: "Sólo permite letras, números, espacios y algunos caracteres especiales ( @ & ¡! ¿? # % / + - _ ' : ; , . )",
												value: /^([A-ZÁ-ÚÄ-ÜÑ0-9@&!¡¿?#%/+\-_':;,.\s]{1,60})$/i
											}}
											minLength={{
												message: "La longitud debe ser entre 1 y 60 caracteres",
												value: 1
											}}
											required
										/>
									</div>
									<div className="col-6">
										<SelectControl
											name="tipoPersona"
											label="Tipo de persona"
											items={tipoPersona}
											formMethods={methods}
											required
										/>
									</div>
								</div>
								<div className="row mt-3 m-auto">
									<div className="col-6">
										<EmailControl
											name="correoElectronico"
											label="Correo electrónico"
											maxLength={60}
											formMethods={methods}
											required
										/>
									</div>
									<div className="col-6">
										<SelectControl
											name="tipoIdentificacion"
											label="Tipo de identificación"
											items={tipoIdentificacion}
											formMethods={methods}
											required
										/>
									</div>
								</div>
								<div className="row mt-3 m-auto">
									<div className="col-6">
										<PhoneControl
											name="telefono"
											label="Teléfono"
											formMethods={methods}
											required
										/>
									</div>
									<div className="col-6">
										<TextControl
											name="numeroIdentificacion"
											label="Número de identificación"
											maxLength={20}
											formMethods={methods}
											pattern={{
												message: "Sólo permite letras y números",
												value: /^([A-Z0-9]{1,20})$/i
											}}
											minLength={{
												message: "La longitud debe ser entre 1 y 20 caracteres",
												value: 1
											}}
											required
										/>
									</div>
								</div>
								<div className="row mt-3 m-auto">
									<div className="col-6">
										<RfcCurpControl
											name="rfcCurp"
											label="RFC o CURP"
											formMethods={methods}
											required
										/>
									</div>
								</div>
								<hr />
								<div className="c-b-success">
									<div className="e-title text-left m-3">
										<span>Datos de tarjeta</span>
									</div>
								</div>
								<div className="row mt-3 m-auto">
									<div className="col-6">
										<NumericControl
											name="cuentaTarjetaClabe"
											label="Cuenta, tarjeta o CLABE"
											maxLength={18}
											formMethods={methods}
											pattern={{
												message: "Debes indicar un número de cuenta, tarjeta o CLABE válido",
												value: /^(\d{10}|\d{16}|\d{18})$/i
											}}
											allowLeadingZeros
											required
										/>
									</div>
									<div className="col-6">
										<MoneyControl
											name="montoMaximo"
											label="Monto máximo del cargo autorizado"
											limit={9999999.99}
											formMethods={methods}
											required
										/>
									</div>
								</div>
								<div className="row mt-3 m-auto">
									<div className="col-6">
										<SelectControl
											name="claveBanco"
											label="Banco"
											items={bancosOptions}
											formMethods={methods}
											required
										/>
									</div>
									<div className="col-6">
										<MultilineControl
											name="concepto"
											label="Concepto de pago"
											maxLength={500}
											formMethods={methods}
											pattern={{
												message: "Sólo permite letras, números, espacios y algunos caracteres especiales ( @ & ¡! ¿? # % / + - _ ' : ; , . )",
												value: /^([A-ZÁ-ÚÄ-ÜÑ0-9@&!¡¿?#%/+\-_':;,.\s]{1,500})$/i
											}}
											minLength={{
												message: "La longitud debe ser entre 1 y 500 caracteres",
												value: 1
											}}
											required
										/>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</Modal>
	);
};

export default DomiciliarPagoModal;